import { useConstant } from 'src/core/hooks/react-hooks';
import { useHistoryLocationObservable } from 'src/hooks/router-hooks';
import {
    addSelection,
    AggregationSelections,
    MappedAggregations,
    removeSelection,
    selectionsFromSearch,
    serializeSelectionsToUrl,
} from 'src/components/CategoryListingPage/utils/cleanAggregations';
import { useDispatch } from 'react-redux';
import { BehaviorSubject, merge, of, Subject } from 'rxjs';
import { useDeps } from 'src/hooks/useDeps';
import { useCallback, useEffect, useRef, useState } from 'react';
import { map, switchMap, tap, withLatestFrom, catchError } from 'rxjs';
import { createDebug } from 'src/util/debug';

const debug = createDebug('useLayeredNav');

export function useLayeredNav(mappedAggregations: MappedAggregations, scrollSelector = '[data-smooth-scroll]') {
    const location = useHistoryLocationObservable();
    const selected = selectionsFromSearch(mappedAggregations, location.value.search);
    const dispatch = useDispatch();
    const selection$ = useConstant(() => new BehaviorSubject<AggregationSelections>(selected));
    const change$ = useConstant(() => new Subject<{ checked: boolean; code: string; label: string }>());
    const removeAll$ = useConstant(() => new Subject<any>());
    const { replace } = useDeps();
    const [filtersSelected, setFiltersSelected] = useState(false);

    useEffect(() => {
        selection$.next(selected);
        if (Object.keys(selected).length > 0) {
            setFiltersSelected(true);
        } else {
            setFiltersSelected(false);
        }
    }, [selected, selection$, location.value.search]);

    useEffect(() => {
        const sub = selection$.subscribe((selections) => {
            if (Object.keys(selections).length > 0) {
                setFiltersSelected(true);
            } else {
                setFiltersSelected(false);
            }
        });
        return () => sub.unsubscribe();
    }, [selection$]);

    useEffect(() => {
        function createNextUrl(prevSelections: AggregationSelections): string {
            const { pathname, search } = window.location;

            const params = serializeSelectionsToUrl(mappedAggregations, prevSelections, search);
            const nextUrl = `${pathname}?${params}`;

            return nextUrl;
        }

        /**
         * Handle a 'remove all' event.
         * This will look at all options and deselect them in the dom,
         * without triggering any events (as we don't want to cause multiple urls updates)
         */
        const removes$ = removeAll$.pipe(
            withLatestFrom(selection$),
            tap(([, prevSelections]) => {
                Object.keys(prevSelections).forEach((key) => {
                    const match = document.querySelectorAll(`input[id^="${key}"]`) as NodeListOf<HTMLInputElement>;
                    match.forEach((elem: HTMLInputElement) => {
                        elem.checked = false;
                    });
                });
            }),
            map(() => ({})),
        );
        /**
         * The update event is when a checkbox goes from 'checked'
         * to 'unchecked' (or the opposite)
         */
        const updates$ = change$.pipe(
            withLatestFrom(selection$),
            map(([change, prevState]) => {
                if (change.checked) {
                    const result = addSelection(prevState, change.code, change.label);
                    return result;
                }
                const result = removeSelection(prevState, change.code, change.label);
                return result;
            }),
        );
        /**
         * Now listen to remove + update events and update the URL to match
         */
        const sub = merge(updates$, removes$)
            .pipe(
                tap((selections) => {
                    debug('next selections = %o', selections);
                    selection$.next(selections);
                }),
                switchMap((selections) => {
                    const result = of(selections);

                    return result;
                }),
                catchError((error) => {
                    return of({});
                }),
                tap((selections) => {
                    const scrollTop = window.pageYOffset || document.documentElement.scrollTop;
                    if (scrollTop > 50) {
                        window.scrollTo({
                            top: 0,
                            behavior: 'smooth',
                        });
                    }

                    return dispatch(replace(createNextUrl(selections)));
                }),
            )
            .subscribe();

        return () => {
            sub.unsubscribe();
        };
    }, [change$, dispatch, mappedAggregations, removeAll$, replace, scrollSelector, selection$]);

    const onChange = useCallback(
        (checked, code, label) => {
            change$.next({ checked, code, label });
        },
        [change$],
    );
    const ref = useRef<HTMLDivElement | null>(null);
    const onRemove = useCallback(
        (code, value, label) => {
            // For multipack, we need to pass the value instead of label to match what's stored in selections
            const labelToUse = code === 'multipack' ? value : label;
            change$.next({ checked: false, code, label: labelToUse });
        },
        [change$],
    );
    /**
     * A proxy for removing all selections
     */
    const onRemoveAll = useCallback(() => {
        removeAll$.next(undefined);
    }, [removeAll$]);
    return { ref, onRemove, onRemoveAll, onChange, selected, selection$, filtersSelected };
}
